// Experience for a position: one number ("2"), a range ("2-3") or open-ended ("10+").
// Same rules as the server (backend app/services/experience.py), so mistakes show as you type.
//
// Accepted: "2", "2.5", "2-3", "2 - 3", "2–3", "2 to 3", "2-3 years", "2 yrs - 3 yrs", "10+", "10+ years"
// Stored as: "2", "2.5", "2-3", "10+"

export const MAX_EXPERIENCE_YEARS = 50;
export const EXPERIENCE_MAX_LENGTH = 24;
export const EXPERIENCE_HINT = 'A number or a range, e.g. 3, 2-3 or 10+';

const fmt = (n) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

/** → { label } when valid, { error } when not, {} when blank. */
export function parseExperience(raw) {
  if (raw === null || raw === undefined) return {};
  let text = String(raw).trim().toLowerCase();
  if (!text) return {};

  text = text.replace(/(?<![a-z])(?:years?|yrs?|y)\b\.?/g, ' ');     // "2 yrs - 3 years"
  text = text.trim().replace(/\s*(?:-|–|—|−|‐|‑|~|\bto\b)\s*/g, '-'); // any dash / "to"
  text = text.replace(/\s*\+\s*/g, '+').replace(/\s+/g, '');

  if (/[^0-9.+-]/.test(text)) return { error: 'Use numbers only, like 2, 2-3 or 10+.' };
  if (text.startsWith('-')) return { error: "Experience can't be negative." };
  if ((text.match(/-/g) || []).length > 1 || (text.includes('+') && text.includes('-')) || (text.match(/\+/g) || []).length > 1) {
    return { error: 'Enter one number or one range, like 2-3 or 10+.' };
  }
  if (/\.\d{2,}/.test(text) || text.includes('..') || /\.(?!\d)/.test(text)) {
    return { error: 'Use at most one decimal place, like 2.5.' };
  }

  const num = '(\\d{1,3}(?:\\.\\d)?)';
  let lo;
  let hi;
  let m;
  if ((m = text.match(new RegExp(`^${num}$`)))) {
    lo = parseFloat(m[1]);
    hi = lo;
  } else if ((m = text.match(new RegExp(`^${num}-${num}$`)))) {
    lo = parseFloat(m[1]);
    hi = parseFloat(m[2]);
    if (lo > hi) return { error: `Put the smaller number first, like ${fmt(hi)}-${fmt(lo)}.` };
  } else if ((m = text.match(new RegExp(`^${num}\\+$`)))) {
    lo = parseFloat(m[1]);
    hi = null;
  } else {
    return { error: 'Enter years like 2, 2-3 or 10+.' };
  }
  if (lo > MAX_EXPERIENCE_YEARS || (hi !== null && hi > MAX_EXPERIENCE_YEARS)) {
    return { error: `Experience can't be more than ${MAX_EXPERIENCE_YEARS} years.` };
  }
  if (hi === null) return { label: `${fmt(lo)}+` };
  return { label: lo === hi ? fmt(lo) : `${fmt(lo)}-${fmt(hi)}` };
}

/** Keeps typing sane: only characters that can appear in a valid entry, and a length cap. */
export function cleanExperienceInput(value) {
  return String(value || '')
    .replace(/[^0-9a-zA-Z .+\-–—~]/g, '')
    .slice(0, EXPERIENCE_MAX_LENGTH);
}

/** Older campaigns only stored a level; show a sensible starting value for those. */
export function experienceFromLevel(level) {
  return { junior: '0-2', mid: '2-5', senior: '5-10', expert: '10+' }[level] || '';
}

/** Readable message from an API error, including FastAPI's 422 field errors ("Value error, …"). */
export function apiErrorText(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length) {
    const first = detail[0] || {};
    const field = Array.isArray(first.loc) ? first.loc[first.loc.length - 1] : '';
    const msg = String(first.msg || '').replace(/^Value error,\s*/i, '');
    if (msg) return field === 'experience_years' ? `Experience: ${msg}` : msg;
  }
  return fallback;
}
